import { memo, type MouseEvent, useCallback } from "react";

import { type IconName } from "@react/components/Icon";
import { CountrySelector } from "@react/sections/DrawerProvider/CountrySelector";
import { type EmailFormProps } from "@react/sections/Footer/FooterEmailForm";
import { useSectionSettings } from "@react/sections/Root/SectionSettingsContext";
import {
  type MarketCountry,
  type MarketLanguage,
  type MarketIsoCode,
} from "@root/tablescape/constants";
import {
  TablescapeContext,
  TablescapeEvent,
} from "@root/tablescape/utilities/tablescapeContext";
import { FooterView } from "@sections/Footer/Footer.view";
import { GlobalDrawer } from "@tablescape/state";

export type Menu = {
  links: {
    title: string;
    url: string;
  }[];
  title: string;
};

export type LocalizationInfo = {
  country: MarketCountry;
  isoCode: MarketIsoCode;
  language: MarketLanguage["name"];
};

export type SocialMediaLink = {
  iconName: IconName;
  url: string;
};

export type FooterSectionSettings = {
  countrySelector: {
    text: {
      header: string;
      label: string;
      location: string;
      shipping: string;
    };
  };
  disclaimer: string;
  emailForm: {
    disclaimer: string;
  } & EmailFormProps;
  legalMenu: Menu;
  localization: LocalizationInfo;
  menus: Menu[];
  socialMediaLinks: SocialMediaLink[];
  subtitle: string;
  title: string;
};

type props = {
  cookieSettingsLabel: string;
};

const FooterViewBase = ({ cookieSettingsLabel }: props) => {
  const { countrySelector, localization, ...rest } =
    useSectionSettings<FooterSectionSettings>();

  const openCountrySelectorHandler = useCallback(() => {
    TablescapeContext.emit(TablescapeEvent.TOGGLE_GLOBAL_DRAWER, {
      ui: {
        globalDrawerTarget: GlobalDrawer.COUNTRY_SELECTOR,
      },
    });
  }, []);

  const handleOneTrustMenuClick = useCallback(
    (e: MouseEvent<HTMLButtonElement> & { target: HTMLButtonElement }) => {
      e.preventDefault();

      window.Optanon?.ToggleInfoDisplay();
    },
    []
  );

  return (
    <>
      <FooterView
        cookieSettingsLabel={cookieSettingsLabel}
        handleOneTrustMenuClick={handleOneTrustMenuClick}
        localization={localization}
        openCountrySelector={openCountrySelectorHandler}
        {...rest}
      />
      <CountrySelector
        isoCode={localization.isoCode}
        text={{
          ...countrySelector.text,
          label: countrySelector.text.label,
        }}
      />
    </>
  );
};

export const Footer = memo(FooterViewBase);
